'use client'

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { useTranslations } from 'next-intl'
import { ImagePlus, Loader2, ThumbsDown, ThumbsUp, X } from 'lucide-react'
import { toast } from 'sonner'

import { submitFeedback, type FeedbackSentiment } from '@/lib/feedback-actions'
import { getOrCreateSupportTraceId } from '@/lib/support-trace'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import {
  clampPosition,
  DEFAULT_POSITION,
  DRAG_THRESHOLD,
  POSITION_STORAGE_KEY,
  readStoredPosition,
  type Size,
  type WidgetPosition,
} from '@/components/feedback/feedback-position'
import { cn } from '@/lib/utils'

const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024
const MAX_SCREENSHOTS = 6

/**
 * `submitFeedback` failure code -> key under `Common.feedback.errors`.
 * Unmapped codes fall back to `submitFailed`, so a new backend code never
 * puts its own text on screen.
 */
const FEEDBACK_ERROR_KEYS = {
  not_signed_in: 'errors.notSignedIn',
  screenshot_required: 'errors.screenshotNeeded',
  screenshot_invalid: 'errors.screenshotBad',
  submit_failed: 'errors.submitFailed',
} as const

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

function viewportSize(): Size {
  return { width: window.innerWidth, height: window.innerHeight }
}

/**
 * Drag the floating pill anywhere in the viewport and remember where.
 *
 * Offsets are kept from the right / bottom edges so the pill keeps its
 * relationship to the corner it lives in when the window is resized, and
 * are re-clamped whenever the viewport or the pill's own size changes, so a
 * narrower window can never strand it off-screen. A drag suppresses the
 * click that would otherwise open the popover (`draggedRef`), which is why
 * the press is tracked here rather than handed to a generic drag library.
 */
function useDraggablePosition(enabled: boolean) {
  const [position, setPosition] = useState<WidgetPosition>(DEFAULT_POSITION)
  const [dragging, setDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const draggedRef = useRef(false)
  // Last size measured while the pill was actually rendered. It is hidden
  // (`display: none`) below `md` and while a modal is open, and a hidden
  // element measures 0×0 — clamping against that lets the pill sit a full
  // viewport away from its edge, which is exactly off-screen. Remembering
  // the real size keeps every clamp honest; `null` means "never seen it
  // rendered", and then the clamp is skipped rather than guessed.
  const sizeRef = useRef<Size | null>(null)
  const activePointerRef = useRef<number | null>(null)

  const clampToViewport = useCallback(() => {
    const size = sizeRef.current
    if (!size) return
    const viewport = viewportSize()
    setPosition(prev => clampPosition(prev, size, viewport))
  }, [])

  // Restore from a ref callback rather than an effect: the first render is
  // server-rendered, so the pill has to stay at the default until the node
  // exists on the client. The stored value is applied as-is and clamped by
  // the observer below, which fires as soon as the size is known.
  const restoredRef = useRef(false)
  const setContainer = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node
    if (!node || restoredRef.current) return
    restoredRef.current = true
    const stored = readStoredPosition(window.localStorage)
    if (stored) setPosition(stored)
  }, [])

  // Re-clamp when the pill's own box changes — which covers both the first
  // measurement and the moment it comes back from `display: none` after a
  // modal closes or the window widens past `md`. A window resize that
  // happened while it was hidden would otherwise never be corrected.
  useEffect(() => {
    const node = containerRef.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(entries => {
      const rect = entries[0]?.contentRect
      if (!rect || rect.width === 0 || rect.height === 0) return
      sizeRef.current = { width: rect.width, height: rect.height }
      clampToViewport()
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [clampToViewport])

  useEffect(() => {
    window.addEventListener('resize', clampToViewport)
    return () => window.removeEventListener('resize', clampToViewport)
  }, [clampToViewport])

  function onPointerDown(event: ReactPointerEvent<HTMLElement>) {
    // Left button / primary contact only, one drag at a time, and never
    // while the popover is open: the popover is positioned against the
    // trigger and would detach mid-drag. Without the primary / active
    // guards a second finger on a tablet would start a parallel drag —
    // every touch contact reports `button === 0` — and the two gestures
    // would fight over the same position and persist whichever lands last.
    if (!enabled || event.button !== 0 || !event.isPrimary) return
    if (activePointerRef.current !== null) return
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    const pointerId = event.pointerId
    const startX = event.clientX
    const startY = event.clientY
    const startPosition = { right: position.right, bottom: position.bottom }
    // A drag can only start while the pill is visible, so this measurement
    // is trustworthy; keep it for the clamps that run without a pointer.
    const size = { width: rect.width, height: rect.height }
    sizeRef.current = size
    let latest = startPosition
    draggedRef.current = false
    activePointerRef.current = pointerId
    const target = event.currentTarget
    target.setPointerCapture(pointerId)

    function onMove(moveEvent: globalThis.PointerEvent) {
      if (moveEvent.pointerId !== pointerId) return
      const dx = moveEvent.clientX - startX
      const dy = moveEvent.clientY - startY
      if (!draggedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
      if (!draggedRef.current) {
        draggedRef.current = true
        setDragging(true)
      }
      // Right / bottom offsets grow as the pointer moves left / up.
      latest = clampPosition(
        { right: startPosition.right - dx, bottom: startPosition.bottom - dy },
        size,
        viewportSize(),
      )
      setPosition(latest)
    }

    function onUp(upEvent: globalThis.PointerEvent) {
      if (upEvent.pointerId !== pointerId) return
      target.releasePointerCapture?.(pointerId)
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onUp)
      target.removeEventListener('pointercancel', onUp)
      activePointerRef.current = null
      if (!draggedRef.current) return
      setDragging(false)
      // The click that follows this pointerup fires synchronously, so the
      // capture handler below still sees the flag; clearing it on the next
      // task keeps a cancelled gesture (no click at all) from swallowing
      // the user's next real click.
      window.setTimeout(() => {
        draggedRef.current = false
      }, 0)
      try {
        window.localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(latest))
      } catch {
        // Storage may be full or blocked; the pill still moved for this
        // session, it just won't be remembered.
      }
    }

    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onUp)
    target.addEventListener('pointercancel', onUp)
  }

  function onClickCapture(event: {
    preventDefault: () => void
    stopPropagation: () => void
  }) {
    // The click that ends a drag must not toggle the popover.
    if (!draggedRef.current) return
    event.preventDefault()
    event.stopPropagation()
  }

  return { setContainer, position, dragging, onPointerDown, onClickCapture }
}

export function FeedbackWidget() {
  const t = useTranslations('Common.feedback')
  const [open, setOpen] = useState(false)
  const [sentiment, setSentiment] = useState<FeedbackSentiment | null>(null)
  const [description, setDescription] = useState('')
  const [screenshots, setScreenshots] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { setContainer, position, dragging, onPointerDown, onClickCapture } =
    useDraggablePosition(!open && !submitting)

  function reset() {
    setSentiment(null)
    setDescription('')
    setScreenshots([])
    setSubmitting(false)
  }

  async function ingestImageFiles(files: File[]) {
    if (files.length === 0) return
    // Reserve remaining capacity in a snapshot so concurrent ingests don't
    // race past MAX_SCREENSHOTS via stale `screenshots` reads.
    let remaining = MAX_SCREENSHOTS - screenshots.length
    if (remaining <= 0) {
      toast.error(t('errors.tooMany', { max: MAX_SCREENSHOTS }))
      return
    }
    const accepted: string[] = []
    let reachedLimit = false
    for (const file of files) {
      if (remaining <= 0) {
        reachedLimit = true
        break
      }
      if (!file.type.startsWith('image/')) {
        toast.error(t('errors.notImage'))
        continue
      }
      if (file.size > MAX_SCREENSHOT_BYTES) {
        toast.error(t('errors.tooLarge'))
        continue
      }
      try {
        const dataUrl = await readFileAsDataUrl(file)
        accepted.push(dataUrl)
        remaining -= 1
      } catch {
        toast.error(t('errors.readFailed'))
      }
    }
    if (accepted.length > 0) {
      setScreenshots(prev => [...prev, ...accepted].slice(0, MAX_SCREENSHOTS))
    }
    if (reachedLimit) {
      toast.error(t('errors.tooMany', { max: MAX_SCREENSHOTS }))
    }
  }

  function handleFileInput(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files ? Array.from(event.target.files) : []
    event.target.value = ''
    if (files.length > 0) void ingestImageFiles(files)
  }

  function removeScreenshotAt(index: number) {
    setScreenshots(prev => prev.filter((_, i) => i !== index))
  }

  // Listen for paste at the window level while the popover is open, so
  // users don't have to click into the textarea before pasting (Cmd+V).
  // Only intercept image pastes — text pastes fall through to the focused
  // input as normal. A single paste may carry multiple image items.
  useEffect(() => {
    if (!open) return
    function onWindowPaste(event: globalThis.ClipboardEvent) {
      const items = event.clipboardData?.items
      if (!items) return
      const files: File[] = []
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile()
          if (file) files.push(file)
        }
      }
      if (files.length > 0) {
        event.preventDefault()
        void ingestImageFiles(files)
      }
    }
    window.addEventListener('paste', onWindowPaste)
    return () => window.removeEventListener('paste', onWindowPaste)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, screenshots.length])

  async function handleSubmit() {
    if (!sentiment || screenshots.length === 0) return
    setSubmitting(true)

    const result = await submitFeedback({
      sentiment,
      description,
      brandSlug:
        /^\/o\/[^/]+\/b\/([^/]+)/.exec(window.location.pathname)?.[1] ?? null,
      url: window.location.href,
      userAgent: navigator.userAgent,
      traceId: getOrCreateSupportTraceId(),
      screenshotDataUrls: screenshots,
      // Build-time identifiers injected by next.config.ts. Empty strings in
      // local dev fall through as undefined so the action skips them.
      releaseVersion: process.env.NEXT_PUBLIC_RELEASE_VERSION || undefined,
      gitCommit: process.env.NEXT_PUBLIC_GIT_COMMIT || undefined,
      buildTime: process.env.NEXT_PUBLIC_BUILD_TIME || undefined,
    })

    if (!result.ok) {
      // The action's `error` is a diagnostic; the code picks the sentence.
      // Same shape the client-side screenshot checks above already use.
      toast.error(
        t(
          FEEDBACK_ERROR_KEYS[
            result.code as keyof typeof FEEDBACK_ERROR_KEYS
          ] ?? 'errors.submitFailed',
        ),
      )
      setSubmitting(false)
      return
    }
    toast.success(t('thankYou'), { description: t('thankYouDescription') })
    setOpen(false)
    reset()
  }

  const canAddMore = screenshots.length < MAX_SCREENSHOTS

  return (
    // Hidden on mobile: the floating pill sits in the bottom-right corner,
    // which on phones overlaps the full-width chat composer / send button.
    // Feedback stays available from md up where there is room beside content.
    // Also hidden while a modal (Sheet / Dialog) is open — react-remove-scroll
    // marks the body `data-scroll-locked`, and the pill would otherwise sit on
    // top of a sheet's bottom-right action (e.g. the Skills install button)
    // while being unclickable anyway (the overlay blocks pointer events).
    <div
      ref={setContainer}
      className="fixed z-[100] max-md:hidden [body[data-scroll-locked]_&]:hidden"
      style={{ right: position.right, bottom: position.bottom }}
    >
      <Popover
        open={open}
        onOpenChange={next => {
          if (submitting) return
          setOpen(next)
        }}
      >
        <PopoverTrigger asChild>
          <Button
            size="sm"
            variant="secondary"
            className={cn(
              'touch-none rounded-full shadow-md',
              dragging ? 'cursor-grabbing' : 'cursor-grab',
            )}
            aria-label={t('sendFeedback')}
            disabled={submitting}
            onPointerDown={onPointerDown}
            onClickCapture={onClickCapture}
          >
            {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
            {submitting ? t('sending') : t('feedback')}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="w-80 space-y-3">
          <div className="space-y-1">
            <p className="text-sm font-medium">{t('sendFeedback')}</p>
            <p className="text-muted-foreground text-xs">{t('description')}</p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={sentiment === 'up' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => setSentiment('up')}
              disabled={submitting}
            >
              <ThumbsUp className="size-4" />
              {t('good')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={sentiment === 'down' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => setSentiment('down')}
              disabled={submitting}
            >
              <ThumbsDown className="size-4" />
              {t('bad')}
            </Button>
          </div>
          <Textarea
            placeholder={t('textareaPlaceholder')}
            value={description}
            onChange={e => setDescription(e.target.value)}
            disabled={submitting}
            maxLength={2000}
            rows={4}
          />
          {screenshots.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {screenshots.map((src, index) => (
                <div key={index} className="relative">
                  <img
                    src={src}
                    alt={`Screenshot ${index + 1}`}
                    className="aspect-square w-full rounded-md border object-cover"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute top-1 end-1 size-5 rounded-full shadow"
                    onClick={() => removeScreenshotAt(index)}
                    disabled={submitting}
                    aria-label={t('removeScreenshot')}
                  >
                    <X className="size-3" />
                  </Button>
                </div>
              ))}
              {canAddMore && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting}
                  className="text-muted-foreground hover:bg-muted hover:text-foreground flex aspect-square w-full items-center justify-center rounded-md border border-dashed transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={t('addMore')}
                >
                  <ImagePlus className="size-4" />
                </button>
              )}
            </div>
          )}
          {screenshots.length === 0 && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
              className="text-muted-foreground hover:bg-muted hover:text-foreground flex w-full items-center gap-2 rounded-md border border-dashed p-3 text-start text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ImagePlus className="size-4 shrink-0" />
              <span>{t('pasteHint')}</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileInput}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setOpen(false)
                reset()
              }}
              disabled={submitting}
            >
              {t('cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSubmit}
              disabled={!sentiment || screenshots.length === 0 || submitting}
            >
              {submitting ? t('sending') : t('send')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
